import { CheckIcon, ChevronDownIcon, CircleAlertIcon, DatabaseIcon, LoaderCircleIcon } from "lucide-react";
import type React from "react";

import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

export interface ToolPartLike {
  type: string;
  toolName?: string;
  state?: string;
  input?: unknown;
  errorText?: string;
}

export function isToolPart(part: { type: string }): part is ToolPartLike {
  return part.type === "dynamic-tool" || part.type.startsWith("tool-");
}

function toolName(part: ToolPartLike): string {
  return part.type === "dynamic-tool" ? (part.toolName ?? "outil") : part.type.replace(/^tool-/, "");
}

/** Claim tools — everything else is aggregate (typed datalake tools, or MCP SQL exploration). */
const CLAIM_TOOLS = new Set([
  "findClaims",
  "getClaimContext",
  "listMyClaims",
  // Outils profonds, réservés aux personas internes. Les oublier ici ferait
  // étiqueter une lecture de pièces en « Analyse du datalake ».
  "getClaimReservationFacts",
  "getClaimLegalFile",
  "getClaimFinancials",
  "listClaimDocuments",
  "readClaimDocument",
  "listClaimCorrespondence",
  "readCorrespondenceMessage",
  "getClaimNotesAndEvents",
  "getClaimAcquisition",
]);

/**
 * What the chain actually did.
 *
 * This header used to be hardcoded to "Analyse du datalake", from when MCP reporting tools were
 * the only ones. It then labelled a `findClaims` + `getClaimContext` chain — the claim path —
 * as a datalake analysis, which is precisely the confusion the assistant's design avoids: a
 * claim's facts never come from aggregates.
 */
function chainLabel(parts: ToolPartLike[]): string {
  const names = parts.map(toolName);
  const claimCalls = names.filter((name) => CLAIM_TOOLS.has(name)).length;

  if (claimCalls === names.length) {
    return "Consultation du dossier";
  }
  if (claimCalls === 0) {
    return "Analyse du datalake";
  }
  return "Dossier et datalake";
}

function isRunning(part: ToolPartLike): boolean {
  return part.state === "input-streaming" || part.state === "input-available";
}

/** Résumé une-ligne des arguments (SQL, nom de rapport…) — tronqué, jamais bloquant. */
function inputSummary(part: ToolPartLike): string | null {
  if (part.input === undefined || part.input === null) {
    return null;
  }
  try {
    const raw =
      typeof part.input === "object" && part.input !== null
        ? Object.values(part.input as Record<string, unknown>).find((value) => typeof value === "string")
        : part.input;
    const text = typeof raw === "string" ? raw : JSON.stringify(part.input);
    if (!text) {
      return null;
    }
    const flat = text.replace(/\s+/g, " ").trim();
    return flat.length > 120 ? `${flat.slice(0, 120)}…` : flat;
  } catch {
    return null;
  }
}

/** Texte avec balayage lumineux (shimmer v0) — pour les états « en cours ». */
export function ShimmerText({ children }: { children: React.ReactNode }) {
  return (
    <span className="animate-text-shimmer bg-gradient-to-r from-gray-500 via-gray-900 to-gray-500 bg-[length:200%_100%] bg-clip-text font-medium text-transparent">
      {children}
    </span>
  );
}

function ToolRow({ part }: { part: ToolPartLike }) {
  const failed = part.state === "output-error";
  const running = isRunning(part);
  const summary = inputSummary(part);

  return (
    <div className="py-1.5 text-xs first:pt-0.5 last:pb-0.5">
      <div className="flex items-center gap-2">
        {failed ? (
          <CircleAlertIcon className="size-3.5 shrink-0 text-rose-500" />
        ) : running ? (
          <LoaderCircleIcon className="size-3.5 shrink-0 animate-spin text-blue-500" />
        ) : (
          <CheckIcon className="size-3.5 shrink-0 text-emerald-500" />
        )}
        <span className={cn("font-mono font-medium", failed ? "text-rose-600" : "text-gray-700")}>
          {toolName(part)}
        </span>
      </div>
      {summary ? (
        <p className="mt-0.5 truncate pl-5.5 font-mono text-[11px] text-gray-400">{summary}</p>
      ) : null}
      {failed && part.errorText ? (
        <p className="mt-0.5 pl-5.5 text-[11px] text-rose-500">{part.errorText}</p>
      ) : null}
    </div>
  );
}

/**
 * Chaîne d'appels d'outils MCP consécutifs, repliée en une ligne de statut
 * (shimmer + outil courant pendant l'exécution), dépliable pour voir chaque
 * requête avec ses arguments — façon "thinking" Vercel/v0.
 */
export function ToolChain({ parts }: { parts: ToolPartLike[] }): React.ReactElement {
  const runningPart = parts.find(isRunning);
  const failedCount = parts.filter((part) => part.state === "output-error").length;

  return (
    <Collapsible className="my-2 animate-in fade-in duration-200">
      <CollapsibleTrigger className="group flex w-full cursor-pointer items-center gap-2 rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2 text-left text-xs transition-colors hover:bg-gray-100/80">
        {runningPart ? (
          <LoaderCircleIcon className="size-3.5 shrink-0 animate-spin text-blue-500" />
        ) : failedCount > 0 ? (
          <CircleAlertIcon className="size-3.5 shrink-0 text-rose-500" />
        ) : (
          <DatabaseIcon className="size-3.5 shrink-0 text-gray-400" />
        )}
        {runningPart ? (
          <ShimmerText>
            {chainLabel(parts)} — <span className="font-mono">{toolName(runningPart)}</span>
          </ShimmerText>
        ) : (
          <span className={cn("font-medium", failedCount > 0 ? "text-rose-600" : "text-gray-500")}>
            {failedCount > 0
              ? `${chainLabel(parts)} — ${failedCount} erreur${failedCount > 1 ? "s" : ""}`
              : chainLabel(parts)}
          </span>
        )}
        <span className="text-gray-400">
          · {parts.length} requête{parts.length > 1 ? "s" : ""}
        </span>
        <ChevronDownIcon className="ml-auto size-3.5 shrink-0 text-gray-400 transition-transform group-data-panel-open:rotate-180" />
      </CollapsibleTrigger>
      <CollapsiblePanel>
        <div className="mt-1 divide-y divide-gray-50 rounded-lg border border-gray-100 px-3 py-1">
          {parts.map((part, index) => (
            <ToolRow key={`${toolName(part)}-${index}`} part={part} />
          ))}
        </div>
      </CollapsiblePanel>
    </Collapsible>
  );
}
